/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable no-console */

/**
 * Enum representing different log levels.
 */
export enum LogLevel {
  DEBUG = 0,
  ERROR = 3,
  INFO = 1,
  NONE = 4,
  PROFILE = 5,
  WARN = 2,
}

type LogMethod = (message: string, ...args: any[]) => void;
/**
 * Logger class for handling application logging.
 */
export class Logger {
  public static level: LogLevel = LogLevel.NONE;
  public static bundleName = 'UNKNOWN';
  public static wgsCustomDomain: string;
  public static environment = 'UNKNOWN';
  public static isWorker =
    typeof self !== 'undefined' && typeof window === 'undefined';
  public static useUniqueProfileKeys = true;
  private static profileTimers: Record<string, number> = {};
  private static scriptStartTime: number = performance.now();

  /**
   * Logs a warning for a missing property.
   * @param {string} propertyName - The name of the missing property.
   */
  static warnMissingProperty(propertyName: string): void {
    Logger.log(LogLevel.WARN, `Missing property: ${propertyName}`);
  }

  /**
   * Sets the current log level.
   * @param {LogLevel} level - The log level to set.
   */
  static setLevel(level: LogLevel): void {
    Logger.level = level;
  }

  /**
   * Sets the environment value for logging.
   * @param {string} env - The environment value to set.
   */
  static setEnvironment(env: string): void {
    Logger.environment = env;
  }

  /**
   * Sets the custom domain for WGS.
   * @param {string} domain - The custom domain to set.
   */
  static setWgsCustomDomain(domain: string): void {
    Logger.wgsCustomDomain = domain;
  }

  /**
   * Sets the log level from a string representation.
   * @param {string} level - The string representation of the log level.
   */
  static setLevelAsString(level: string): void {
    const levelEnum = LogLevel[level as keyof typeof LogLevel];
    Logger.info('levelEnum', levelEnum);
    if (levelEnum !== undefined) {
      Logger.level = levelEnum;
    }
  }

  /**
   * Sets the bundle name for logging.
   * @param {string} name - The bundle name to set.
   */
  static setBundleName(name: string): void {
    Logger.bundleName = name;
  }

  /**
   * Enables or disables the use of unique profile keys.
   * @param {boolean} enable - Whether to enable unique profile keys.
   */
  static setUseUniqueProfileKeys(enable: boolean): void {
    Logger.useUniqueProfileKeys = enable;
  }

  /**
   * Resets the script start time to the current time.
   * Useful when you want to measure from a specific point in the application lifecycle.
   */
  static resetScriptStartTime(): void {
    Logger.scriptStartTime = performance.now();
  }

  /**
   * Gets the time in milliseconds since the script started.
   * @returns {number} Time in milliseconds since script start.
   */
  static getTimeSinceScriptStart(): number {
    return Number((performance.now() - Logger.scriptStartTime).toFixed(2));
  }

  /**
   * Get the thread context indicator for logging.
   * @returns {string} String indicating whether this is a main thread or worker thread.
   */
  private static getThreadContext(): string {
    return Logger.isWorker ? 'wrk' : 'main';
  }

  /**
   * Check if the given log level should be logged based on the current settings.
   * @param {LogLevel} level - The log level to check.
   * @returns {boolean} Whether the level should be logged.
   */
  private static shouldLog(level: LogLevel): boolean {
    return !(
      (level < Logger.level || Logger.level === LogLevel.NONE) &&
      process.env['VITE_APP_IS_DEBUG'] !== 'true'
    );
  }

  /**
   * Get a formatted prefix for log messages.
   * @returns {string} The formatted prefix.
   */
  private static getLogPrefix(): string {
    const threadContext = Logger.getThreadContext();
    return `[${Logger.environment.toLowerCase()}:${threadContext}] [${Logger.bundleName}]`;
  }

  /**
   * Internal method for logging messages.
   * @param {LogLevel} level - The log level of the message.
   * @param {string} message - The message to log.
   * @param {...any[]} args - Additional arguments to log.
   */
  private static log(level: LogLevel, message: string, ...args: any[]): void {
    if (!Logger.shouldLog(level)) return;

    const logMethods: Record<LogLevel, LogMethod> = {
      [LogLevel.DEBUG]: console.debug,
      [LogLevel.ERROR]: console.error,
      [LogLevel.INFO]: console.info,
      [LogLevel.NONE]: () => {},
      [LogLevel.PROFILE]: console.info,
      [LogLevel.WARN]: console.warn,
    };

    const prefix = Logger.getLogPrefix();
    logMethods[level](prefix, message, ...args);
  }

  /**
   * Logs a debug message.
   * @param {string} message - The message to log.
   * @param {...any[]} args - Additional arguments to log.
   */
  static debug(message: string, ...args: any[]): void {
    Logger.log(LogLevel.DEBUG, message, ...args);
  }

  /**
   * Logs an info message.
   * @param {string} message - The message to log.
   * @param {...any[]} args - Additional arguments to log.
   */
  static info(message: string, ...args: any[]): void {
    Logger.log(LogLevel.INFO, message, ...args);
  }

  /**
   * Logs a warning message.
   * @param {string} message - The message to log.
   * @param {...any[]} args - Additional arguments to log.
   */
  static warn(message: string, ...args: any[]): void {
    Logger.log(LogLevel.WARN, message, ...args);
  }

  /**
   * Logs an error message.
   * @param {string} message - The message to log.
   * @param {...any[]} args - Additional arguments to log.
   */
  static error(message: string, ...args: any[]): void {
    Logger.log(LogLevel.ERROR, message, ...args);
  }

  /**
   * Starts profiling a section with the given key and trace ID.
   * @param {string} key - The key identifying the section to profile.
   * @param {string} traceId - Optional trace ID to group related profile sections.
   */
  static profileStart(key: string, traceId?: string): void {
    const profileKey = traceId ? `${traceId}:${key}` : key;
    // Store manual timer start point
    Logger.profileTimers[profileKey] = performance.now();

    // We don't use console.time anymore to avoid duplicate logs
  }

  /**
   * Ends profiling a section with the given key and trace ID, logging the elapsed time.
   * @param {string} key - The key identifying the section to profile.
   * @param {string} traceId - Optional trace ID to group related profile sections.
   * @returns {number} The elapsed time in milliseconds.
   */
  static profileEnd(key: string, traceId?: string): number {
    const profileKey = traceId ? `${traceId}:${key}` : key;
    const startTime = Logger.profileTimers[profileKey];

    if (startTime === undefined) {
      // Provide more context in the warning message
      const activeTimerKeys = Object.keys(Logger.profileTimers);
      Logger.warn(
        `No profile start time found for key: ${profileKey}. Active timers: ${
          activeTimerKeys.length > 0
            ? activeTimerKeys.slice(0, 5).join(', ')
            : 'none'
        }${activeTimerKeys.length > 5 ? ` and ${activeTimerKeys.length - 5} more` : ''}`,
      );
      return 0;
    }

    const endTime = performance.now();
    const elapsed = endTime - startTime;
    const sinceScriptStart = Logger.getTimeSinceScriptStart();

    // Log a single message if we should log
    if (Logger.shouldLog(LogLevel.PROFILE)) {
      Logger.log(
        LogLevel.PROFILE,
        `[PROFILE] [${profileKey}] ${elapsed.toFixed(2)}ms - ${sinceScriptStart.toFixed(2)}ms since init`,
      );
    }

    delete Logger.profileTimers[profileKey];
    return elapsed;
  }

  /**
   * Profiles the execution of a function.
   * @param {string} key - The key identifying the function to profile.
   * @param {Function} fn - The function to profile.
   * @param {string} traceId - Optional trace ID to group related profile sections.
   * @param {boolean} addUniqueId - Whether to add a unique identifier to the profile key.
   * @returns The result of the function execution.
   */
  static profileFn<T>(
    key: string,
    fn: () => T,
    traceId?: string,
    addUniqueId?: boolean,
  ): T {
    const shouldAddUniqueId =
      addUniqueId === undefined ? Logger.useUniqueProfileKeys : addUniqueId;
    const profileKey = shouldAddUniqueId
      ? `${key}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      : key;

    Logger.profileStart(profileKey, traceId);
    const result = fn();
    Logger.profileEnd(profileKey, traceId);
    return result;
  }

  /**
   * Profiles the execution of an async function.
   * @param {string} key - The key identifying the function to profile.
   * @param {Function} fn - The async function to profile.
   * @param {string} traceId - Optional trace ID to group related profile sections.
   * @param {boolean} addUniqueId - Whether to add a unique identifier to the profile key.
   * @returns Promise containing the result of the async function execution.
   */
  static async profileFnAsync<T>(
    key: string,
    fn: () => Promise<T>,
    traceId?: string,
    addUniqueId?: boolean,
  ): Promise<T> {
    const shouldAddUniqueId =
      addUniqueId === undefined ? Logger.useUniqueProfileKeys : addUniqueId;
    const profileKey = shouldAddUniqueId
      ? `${key}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      : key;

    Logger.profileStart(profileKey, traceId);
    try {
      const result = await fn();
      Logger.profileEnd(profileKey, traceId);
      return result;
    } catch (error) {
      Logger.profileEnd(profileKey, traceId);
      throw error;
    }
  }

  /**
   * Utility method to dump all active profile timers for debugging purposes.
   * @returns {Record<string, number>} A copy of the active profile timers.
   */
  static dumpProfileTimers(): Record<string, number> {
    const timerCount = Object.keys(Logger.profileTimers).length;
    if (timerCount > 0) {
      Logger.debug(`Active profile timers (${timerCount}):`, {
        ...Logger.profileTimers,
      });
    } else {
      Logger.debug('No active profile timers');
    }
    return { ...Logger.profileTimers };
  }
}
